import { useState, useEffect, useCallback } from 'react';

export const useOnboarding = (userId = null) => {
  const [shouldShowOnboarding, setShouldShowOnboarding] = useState(false);
  const [isOnboardingActive, setIsOnboardingActive] = useState(false);

  useEffect(() => {
    const checkOnboardingStatus = () => {
      // Different keys for anonymous vs authenticated users
      const storageKey = userId
        ? `pickaiOnboarding_${userId}`
        : 'pickaiOnboarding_anonymous';

      const hasCompletedKey = `${storageKey}_completed`;
      const hasSkippedKey = `${storageKey}_skipped`;
      const versionKey = `${storageKey}_version`;

      const hasCompleted = localStorage.getItem(hasCompletedKey) === 'true';
      const hasSkipped = localStorage.getItem(hasSkippedKey) === 'true';
      const version = localStorage.getItem(versionKey);
      const currentVersion = '1.0.0';

      // Check if user has seen onboarding before authentication
      const hasSeenAsAnonymous = localStorage.getItem('pickaiOnboarding_anonymous_completed') === 'true';

      // Strategy:
      // 1. First-time anonymous visitors: Show onboarding
      // 2. First-time authenticated users who haven't seen it as anonymous: Show onboarding
      // 3. Users who saw it as anonymous but now authenticated: Don't show again unless version update
      // 4. Version updates: Show to everyone who hasn't skipped

      let shouldShow = false;

      if (!userId) {
        // Anonymous user
        shouldShow = !hasCompleted && !hasSkipped;
      } else {
        // Authenticated user
        if (!hasCompleted && !hasSkipped) {
          // They haven't seen the authenticated version
          if (hasSeenAsAnonymous) {
            // They saw it as anonymous, maybe show a shorter version or skip
            shouldShow = false; // You can change this to true for a "welcome back" tour
          } else {
            // They haven't seen any version
            shouldShow = true;
          }
        }
      }

      // Check for version updates
      if (version !== currentVersion && !hasSkipped) {
        shouldShow = true;
      }

      setShouldShowOnboarding(shouldShow);
    };

    checkOnboardingStatus();
  }, [userId]);

  const startOnboarding = useCallback(() => {
    setIsOnboardingActive(true);
  }, []);

  const completeOnboarding = useCallback(() => {
    const storageKey = userId
      ? `pickaiOnboarding_${userId}`
      : 'pickaiOnboarding_anonymous';

    localStorage.setItem(`${storageKey}_completed`, 'true');
    localStorage.setItem(`${storageKey}_version`, '1.0.0');
    setIsOnboardingActive(false);
    setShouldShowOnboarding(false);
  }, [userId]);

  const skipOnboarding = useCallback(() => {
    const storageKey = userId
      ? `pickaiOnboarding_${userId}`
      : 'pickaiOnboarding_anonymous';

    localStorage.setItem(`${storageKey}_skipped`, 'true');
    setIsOnboardingActive(false);
    setShouldShowOnboarding(false);
  }, [userId]);

  const resetOnboarding = useCallback(() => {
    const storageKey = userId
      ? `pickaiOnboarding_${userId}`
      : 'pickaiOnboarding_anonymous';

    localStorage.removeItem(`${storageKey}_completed`);
    localStorage.removeItem(`${storageKey}_skipped`);
    localStorage.removeItem(`${storageKey}_version`);
    sessionStorage.removeItem('pickaiOnboardingPrompted');
    setShouldShowOnboarding(true);
    setIsOnboardingActive(false);
  }, [userId]);

  const triggerOnboarding = useCallback(() => {
    resetOnboarding();
    setTimeout(() => startOnboarding(), 100);
  }, [resetOnboarding, startOnboarding]);

  return {
    shouldShowOnboarding,
    isOnboardingActive,
    startOnboarding,
    completeOnboarding,
    skipOnboarding,
    resetOnboarding,
    triggerOnboarding,
  };
};